import * as THREE from "three";

type ShadowCasterSource = ( add: ( mesh: THREE.Mesh ) => void ) => void;

export class ShadowBatcher
{
  private static enabled = ShadowBatcher.readEnabled();

  public static readonly last = { casters: 0, groups: 0 };

  private static source: ShadowCasterSource | null = null;
  private static readonly groups = new Map<string, Group>();
  private static readonly lists = new Map<string, THREE.Mesh[]>();
  private static readonly casting: THREE.Mesh[] = [];

  public static isEnabled(): boolean
  {
    return this.enabled;
  }

  public static setEnabled( on: boolean ): void
  {
    this.enabled = on;
    try { localStorage.setItem( "bb_shadow_batching", on ? "1" : "0" ); } catch {}
  }

  public static setSource( source: ShadowCasterSource ): void
  {
    this.source = source;
  }

  public static install( renderer: THREE.WebGLRenderer ): void
  {
    const shadow_map = renderer.shadowMap;
    const render_shadows = shadow_map.render.bind( shadow_map );
    shadow_map.render = ( lights: THREE.Light[], scene: THREE.Scene, camera: THREE.Camera ) =>
    {
      const runs = shadow_map.enabled && ( shadow_map.autoUpdate || shadow_map.needsUpdate ) && lights.length > 0;
      const batched = runs && this.begin( scene );
      try { render_shadows( lights, scene, camera ); }
      finally { if ( batched ) this.end(); }
    };
  }

  private static begin( scene: THREE.Object3D ): boolean
  {
    if ( !this.enabled || !this.source )
      return false;

    for ( const list of this.lists.values() )
      list.length = 0;

    let count = 0;
    this.source( mesh =>
    {
      if ( !mesh.castShadow || !mesh.visible || !mesh.parent || Array.isArray( mesh.material ) )
        return;

      const material = mesh.material as THREE.Material;
      const key = `${mesh.geometry.id}|${material.side}|${material.shadowSide ?? ""}`;
      let list = this.lists.get( key );
      if ( !list )
      {
        list = [];
        this.lists.set( key, list );
      }
      list.push( mesh );
      count++;
    } );

    this.last.casters = count;
    this.last.groups = 0;
    if ( count === 0 )
      return false;

    for ( const [ key, list ] of this.lists )
    {
      if ( list.length === 0 )
        continue;

      let group = this.groups.get( key );
      if ( !group )
      {
        group = new Group( list[0] );
        this.groups.set( key, group );
      }
      group.show( list, scene );
      this.last.groups++;

      for ( const mesh of list )
      {
        mesh.castShadow = false;
        this.casting.push( mesh );
      }
    }

    return true;
  }

  private static end(): void
  {
    for ( const mesh of this.casting )
      mesh.castShadow = true;
    this.casting.length = 0;

    for ( const [ key, group ] of this.groups )
    {
      group.hide();
      if ( ( this.lists.get( key )?.length ?? 0 ) === 0 && group.idle() )
      {
        group.dispose();
        this.groups.delete( key );
        this.lists.delete( key );
      }
    }
  }

  private static readEnabled(): boolean
  {
    try { return localStorage.getItem( "bb_shadow_batching" ) !== "0"; } catch { return true; }
  }
}

class Group
{
  private static readonly IDLE_PASSES = 120;

  private mesh: THREE.InstancedMesh;
  private capacity = 0;
  private idle_passes = 0;
  private readonly geometry: THREE.BufferGeometry;
  private readonly material: THREE.MeshBasicMaterial;

  constructor( sample: THREE.Mesh )
  {
    const source = sample.material as THREE.Material;
    this.geometry = sample.geometry;
    this.material = new THREE.MeshBasicMaterial( { side: source.side } );
    this.material.shadowSide = source.shadowSide;
    this.mesh = this.build( 16 );
  }

  public show( list: THREE.Mesh[], scene: THREE.Object3D ): void
  {
    if ( list.length > this.capacity )
    {
      let capacity = this.capacity;
      while ( capacity < list.length )
        capacity *= 2;

      const old = this.mesh;
      this.mesh = this.build( capacity );
      old.removeFromParent();
      old.dispose();
    }

    if ( this.mesh.parent !== scene )
      scene.add( this.mesh );

    const array = this.mesh.instanceMatrix.array as Float32Array;
    for ( let i = 0; i < list.length; i++ )
      array.set( list[i].matrixWorld.elements, i * 16 );

    this.mesh.count = list.length;
    this.mesh.instanceMatrix.clearUpdateRanges();
    this.mesh.instanceMatrix.addUpdateRange( 0, list.length * 16 );
    this.mesh.instanceMatrix.needsUpdate = true;
    this.mesh.visible = true;
    this.idle_passes = 0;
  }

  public hide(): void
  {
    this.mesh.visible = false;
  }

  public idle(): boolean
  {
    return ++this.idle_passes > Group.IDLE_PASSES;
  }

  public dispose(): void
  {
    this.mesh.removeFromParent();
    this.mesh.dispose();
    this.material.dispose();
  }

  private build( capacity: number ): THREE.InstancedMesh
  {
    this.capacity = capacity;

    const mesh = new THREE.InstancedMesh( this.geometry, this.material, capacity );
    mesh.instanceMatrix.setUsage( THREE.DynamicDrawUsage );
    mesh.count = 0;
    mesh.visible = false;
    mesh.castShadow = true;
    mesh.receiveShadow = false;
    mesh.frustumCulled = false;
    mesh.matrixAutoUpdate = false;
    mesh.matrixWorldAutoUpdate = false;
    mesh.userData.noBatch = true;
    mesh.name = "shadow_batcher_group";
    return mesh;
  }
}
